import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { graphql } from 'gatsby';
import { Container, Row, Column, List } from '@pella/sash-framework';
import { Layout, SEO, Breadcrumbs, CTA, Image } from '../../components';
import { useRtaData } from '../../hooks/use-rta-data';
import LocationsMap from '../../components/common/locations-map';
import ScheduleConsultation from '../../components/page/rta/v2/home';
import CardSection from '../../components/common/card-section';
import styles from './index.module.scss';

const WhereToBuy = (props) => {
	const [isLoaded, setIsLoaded] = useState(false);
	const [callHours, setCallHours] = useState({ support: true, storm: true });
	const toggleUrls = [
		{ type: 'Homeowner', url: props.data.homeUrl.url },
		{ type: 'Professional', url: props.data.proUrl?.url },
	];
	const pageData = props.data.contentstackWhereToBuy;
	const rtaData = useRtaData();

	useEffect(() => {
		setIsLoaded(true);

		// Only run if chat button exists (/about/contact/)
		if (document.getElementById('js-chatButton')) {
			checkCallHours();

			let intervalCount = 0;
			let interval = setInterval(() => {
				if (document.querySelector('.quiq-floating-element')) {
					clearInterval(interval);
					document.getElementById('js-chatButton').classList.remove('hidden');
				} else if (intervalCount > 7) {
					clearInterval(interval);
				}
				intervalCount++;
			}, 1500);
		}
	}, []);

	const checkCallHours = () => {
		const date = new Date();
		const hour = date.getHours();
		const day = date.getDay();

		// Support Chat Hours - Mon-Fri 7:30am-4pm
		if (day > 0 && day < 6 && (hour < 7.5 || hour >= 16)) {
			setCallHours((prevState) => ({ ...prevState, support: false }));
			if (document.getElementById('js-textHours')) document.getElementById('js-textHours').classList.add('hidden');
		}

		// Storm Chat Hours - Mon-Fri 7:30am-4:30pm
		if (day > 0 && day < 6 && (hour < 7.5 || hour >= 16.5)) setCallHours((prevState) => ({ ...prevState, storm: false }));

		// Storm Chat Hours - Sat 9am-2pm
		if (day === 6 && (hour < 9 || hour >= 14)) setCallHours((prevState) => ({ ...prevState, storm: false }));
	};

	const onChatButtonClick = (event) => {
		event.preventDefault();
		document.querySelector('.quiq-action-toggle').click();
	};

	return (
		<Layout
			id='template-WhereToBuy'
			pageTitle={pageData.seo?.title}
		>
			{pageData.breadcrumbs && (
				<Breadcrumbs
					data={pageData.breadcrumbs}
					currentUrl={pageData.url}
					className='md:absolute'
				/>
			)}

			{pageData.url !== '/about/contact/' && (
				<div className={`${styles.wtbIntro} overflow-hidden py-2 md:pt-16`}>
					<Container className='relative'>
						{toggleUrls?.[0] && toggleUrls.filter((url) => url.url)?.length > 1 && (
							<div className={`${styles.wtbToggle} flex rounded-sm p-1 text-center md:absolute`}>
								{toggleUrls.map((toggle, index) => (
									<CTA
										key={index}
										data={{ link: { href: toggle.url, title: toggle.type } }}
										className={`${pageData.url === toggle.url ? 'bg-black text-white' : 'text-neutral-600'} w-full flex-1 py-1 font-semibold`}
									/>
								))}
							</div>
						)}

						{pageData.heading && <h1 className={`${styles.wtbPageHeading} relative mt-4 inline md:my-0`}>{pageData.heading}</h1>}
						{pageData.subheading && (
							<p
								className='mt-6 md:text-xl'
								dangerouslySetInnerHTML={{ __html: pageData.subheading }}
							/>
						)}
					</Container>
				</div>
			)}

			{pageData.page_content?.[0] &&
				pageData.page_content.map((block, index) => {
					if (block.maps != null) {
						const isShowroomMap = block.maps.map_type === 'showroom';

						return (
							<LocationsMap
								key={index}
								data={block.maps}
								include={block.maps.map_type === 'pro' ? ['showrooms', 'lowes', 'stores'] : isShowroomMap ? ['showrooms'] : ['showrooms', 'lowes', 'stores', 'contractors']}
								radius={100}
								className={`${isShowroomMap ? styles.showroomMap : 'bg-neutral-50 pt-8'}`}
								containerWidth={isShowroomMap ? 0 : null}
							/>
						);
					}

					if (block.forms != null) {
						return (
							<div
								key={index}
								className='bg-white py-10'
							>
								<Container>
									{block.forms.heading && <h2 className='mb-2 font-semibold uppercase'>{block.forms.heading}</h2>}
									{block.forms.subheading && <p className='mb-6'>{block.forms.subheading}</p>}
									{block.forms.description && (
										<div
											className={styles.wtbFormDesc}
											dangerouslySetInnerHTML={{ __html: block.forms.description }}
										/>
									)}

									<div className={`${styles.wtbForm} mt-8 p-4 md:mt-10 md:p-6 xl:p-10`}>
										{block.forms.form_heading && <p className='mb-6 md:text-xl xl:mb-10'>{block.forms.form_heading}</p>}

										{isLoaded && block.forms.form_type === 'rta' && (
											<ScheduleConsultation
												data={rtaData}
												location={window.location}
												layout='modal'
												ctaText='See Availability'
												referrer='whereToBuy'
											/>
										)}
									</div>
								</Container>
							</div>
						);
					}

					if (block.cards != null) {
						return (
							<CardSection
								data={block.cards}
								key={index}
							/>
						);
					}

					if (block.contact_cards != null) {
						return (
							<div
								key={index}
								className={`pt-6 md:pt-8 xl:pt-10 ${block.contact_cards.background_color === 'gray' ? 'bg-neutral-50' : ''}`}
							>
								<Container>
									{block.contact_cards.card?.[0] && (
										<Row className='space-y-4 md:space-y-0'>
											{block.contact_cards.card.map((card, index) => (
												<Column
													xs={12}
													md={6}
													key={index}
												>
													<div className='flex h-full flex-col bg-white'>
														{card.heading && (
															<div
																dangerouslySetInnerHTML={{ __html: card.heading }}
																className='bg-neutral-600 p-4 text-base font-semibold text-white md:px-6 md:text-lg xl:text-2xl'
															/>
														)}

														<div className='flex h-full flex-col p-4 md:p-6'>
															{card.contact_info && (
																<div
																	className={`${styles.contactCardInfo} ${!callHours.support && index === 0 ? '[&>div>p:first-child]:hidden' : ''}`}
																	dangerouslySetInnerHTML={{ __html: card.contact_info }}
																/>
															)}

															{card.cta?.[0] && (
																<List
																	bullets='none'
																	className='space-y-2.5 pt-4 md:mt-auto md:pt-6'
																>
																	{card.cta.map((cta, index) => {
																		if (cta.link?.href === '#' && !callHours.support) return null;
																		if (cta.link?.href?.includes('chat-with-us') && !callHours.storm) return null;

																		return (
																			<CTA
																				key={index}
																				data={cta}
																				id={cta.cta_id}
																				onClick={cta.cta_id === 'js-chatButton' ? onChatButtonClick : null}
																				className={`flex items-center justify-center [&>span]:mr-2.5 [&>span]:text-lg [&>span]:leading-none ${cta.cta_id === 'js-chatButton' ? 'hidden' : ''}`}
																				size='sm'
																				wide
																			/>
																		);
																	})}
																</List>
															)}
														</div>
													</div>
												</Column>
											))}
										</Row>
									)}
								</Container>
							</div>
						);
					}

					if (block.hero != null) {
						return (
							<div key={index}>
								{block.hero.image?.[0] && (
									<Image
										data={block.hero.image[0]}
										className='w-full 2xl:aspect-[4.5/1]'
									/>
								)}
								{block.hero.heading && (
									<div className='bg-black py-3'>
										<Container>
											<div
												dangerouslySetInnerHTML={{ __html: block.hero.heading }}
												className='font-semibold [&>*]:m-0 [&>*]:text-26px [&>*]:text-white md:[&>*]:text-4xl xl:[&>*]:text-[2.625rem]'
											/>
										</Container>
									</div>
								)}
							</div>
						);
					}
				})}
		</Layout>
	);
};

WhereToBuy.propTypes = {
	data: PropTypes.object,
};

// eslint-disable-next-line react/prop-types -- fix this
export const Head = ({ data }) => (
	<SEO
		// eslint-disable-next-line react/prop-types -- fix this
		{...data.contentstackWhereToBuy.seo}
	/>
);

export default WhereToBuy;

export const query = graphql`
	query ($uid: String!) {
		proUrl: contentstackWhereToBuy(uid: { eq: "blt2bcce490f4cd1609" }) {
			url
		}
		homeUrl: contentstackWhereToBuy(uid: { eq: "bltca9827c9941781c4" }) {
			url
		}

		contentstackWhereToBuy(id: { eq: $uid }) {
			url
			breadcrumbs {
				active_page
				back_pages {
					...CTA
				}
			}
			heading
			subheading
			dynamic {
				...Dynamic
			}
			page_content {
				hero {
					image {
						...Image
						image {
							gatsbyImageData(quality: 90)
							url
						}
					}
					heading
					subheading
				}
				contact_cards {
					background_color
					card {
						heading
						contact_info
						cta {
							...CTA
						}
					}
				}
				maps {
					map_type
					heading
					description
					map_icons {
						location_type
						file {
							url
						}
					}
					file {
						url
						gatsbyImageData(quality: 90)
					}
					subheading
					address
					view_on_map
				}
				forms {
					heading
					subheading
					description
					form_heading
					form_type
				}
				cards {
					...CardSection
				}
			}
			seo {
				title
				description
				noIndex: no_index_page
			}
		}
	}
`;
